import type { ProductAvailabilities_ProductFragment } from '@gql-generated/graphql'
import { useMemo } from 'react'
import { useReferencesProductTableContext } from '../components/references-product/parts/references-product-table/ReferencesProductContext'
import type { MergedArticle } from '../types/mergedProduct'
import {
  isProductAvailableForSale,
  shouldDisplayProductAvailabilities,
  shouldDisplayProductPrices,
} from '../utils/productAvailabilities'
import { getAvailabilityIssue, hasDisplayablePrice } from '../utils/productBuyBox'
import { getProductDisplayPrice } from '../utils/productDisplayPrice'

type UseReferencesProductRowProps = {
  article: MergedArticle
}

/**
 * Price and availability display rules shared by the desktop and mobile rows of the references table.
 */
export function useReferencesProductRow({ article }: UseReferencesProductRowProps) {
  const {
    isPublicPriceEnabled,
    isPublicStockEnabled,
    isAuthenticated,
    isArticlesPriceAndStockLoading,
  } = useReferencesProductTableContext()

  const availabilityIssue = useMemo(
    () =>
      getAvailabilityIssue({
        product: article,
        isPublicPriceEnabled,
        isProduct: false,
        isAuthenticated,
      }),
    [article, isPublicPriceEnabled, isAuthenticated],
  )
  const hasPrice = hasDisplayablePrice({ product: article, isProduct: false, availabilityIssue })

  const displayPrice = useMemo(
    () => getProductDisplayPrice(article, { withErpPrice: true }),
    [article],
  )

  const hasTiersPrices = !!article.stock?.packaging && displayPrice.tiersPrices.length > 0

  // Skip loading state for disconnected users on non-public-price sites, prices won't be shown anyway
  const showPricesAndEcoPart = isAuthenticated || isPublicPriceEnabled
  const effectiveLoading = isArticlesPriceAndStockLoading && showPricesAndEcoPart
  // Guests on public-stock sites still wait for stock data, even though prices stay hidden
  const isAvailabilityLoading =
    isArticlesPriceAndStockLoading && (showPricesAndEcoPart || isPublicStockEnabled)
  const isAvailabilityDisplayable = shouldDisplayProductAvailabilities({
    hasPrice,
    hasAvailabilityIssue: !!availabilityIssue,
    isAuthenticated,
    isPublicStockEnabled,
  })

  const isAvailableForSale =
    isProductAvailableForSale({
      product: article as ProductAvailabilities_ProductFragment,
    }) && hasPrice
  const isProductPriceDisplayable = shouldDisplayProductPrices({
    product: article,
    isPublicPriceEnabled,
    isAuthenticated,
  })

  return {
    hasPrice,
    displayPrice,
    hasTiersPrices,
    effectiveLoading,
    isAvailabilityLoading,
    isAvailabilityDisplayable,
    isAvailableForSale,
    isProductPriceDisplayable,
  }
}
